// Globals
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Modules
import { getFaqChannelsOptions, useGetFaqChannels } from '@/modules/FaqChannels';

// Components
import { Form, FormItem, useForm, useWatchForm } from '@/components';
import { Button, IRadioChangeEvent, Radio, RadioGroup, Select } from '@/ui';
import { StyledButtons } from './ApproveFaqFormStyles';

// Helpers
import { ApproveFaqFieldsNames, TApproveFaqFormValues } from '../../helpers';
import { useApproveFaqFormRules } from '../../hooks';

type TApproveFaqFormProps = {
  initialValues?: Partial<TApproveFaqFormValues>;
  isLoading?: boolean;
  onSubmit: (values: TApproveFaqFormValues) => void;
  onReset: () => void;
};

const ApproveFaqForm: FC<TApproveFaqFormProps> = ({ initialValues, isLoading, onSubmit, onReset }) => {
  const { t } = useTranslation();
  const [formInstance] = useForm();
  const rules = useApproveFaqFormRules(formInstance);
  const { channels, isLoading: isChannelsLoading } = useGetFaqChannels();
  const isGeneral = useWatchForm(ApproveFaqFieldsNames.IS_GENERAL, formInstance);

  const channelOptions = useMemo(() => getFaqChannelsOptions(channels), [channels]);

  const handleTargetChange = (event: IRadioChangeEvent) => {
    if (event.target.value) {
      formInstance.setFieldValue(
        ApproveFaqFieldsNames.CHANNEL_IDS,
        initialValues?.[ApproveFaqFieldsNames.CHANNEL_IDS] || [],
      );
    }
  };

  return (
    <Form form={formInstance} onFinish={onSubmit} onReset={onReset} initialValues={initialValues}>
      <FormItem name={ApproveFaqFieldsNames.IS_GENERAL} label={t('label_approve_target')}>
        <RadioGroup onChange={handleTargetChange}>
          <Radio value>{t('label_publish_general')}</Radio>
          <Radio value={false}>{t('label_publish_channels')}</Radio>
        </RadioGroup>
      </FormItem>

      {!isGeneral && (
        <FormItem
          name={ApproveFaqFieldsNames.CHANNEL_IDS}
          label={t('label_target_channels')}
          rules={rules[ApproveFaqFieldsNames.CHANNEL_IDS]}
        >
          <Select
            loading={isChannelsLoading}
            isMultiselect
            options={channelOptions}
            placeholder={t('placeholder_select_channels')}
          />
        </FormItem>
      )}

      <StyledButtons>
        <Button type="primary" htmlType="submit" block loading={isLoading}>
          {t('btn_approve')}
        </Button>
        <Button type="default" htmlType="button" block onClick={onReset}>
          {t('cancel_btn')}
        </Button>
      </StyledButtons>
    </Form>
  );
};

export default ApproveFaqForm;
